// src/hooks/integrations/synapse/useSynapseImport.ts
import { useCallback, useState } from 'react';

import useApi from '@hooks/useApi';

import { SynapseFile } from '@models/integrations/synapse/SynapseFile';

import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

// Initialize logger at module level instead of inside the hook
const logger = Logger.make('useSynapseImport');

interface ImportSynapseFastqFilesParams {
    experimentId: string;
    fileIds: string[];
}

interface ImportSynapseFastqFilesResponse {
    success: boolean;
    message: string;
    importedFiles?: SynapseFile[];
    sessionId?: string;
}

interface WorkflowStatus {
    status: {
        file_name: string;
        status: string;
    }[];
    overall_status: string;
}

interface Workflow {
    session_id: string;
    created_at: string;
}

interface WorkflowFile {
    file_name: string;
    status: string;
}

interface SynapseProjectInfo {
    id: string;
    name: string;
    description?: string;
    createdOn?: string;
    modifiedOn?: string;
    createdBy?: string;
}

interface SynapseProjectResponse {
    entity: {
        id: string;
        name: string;
        description?: string;
        createdOn?: string;
        modifiedOn?: string;
    };
    entityCreatedBy?: {
        userName: string;
    };
}

/**
 * Hook for importing Synapse FASTQ files into Pluto
 */
const useSynapseImport = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const api = useApi();

    /**
     * Get project information from Synapse
     *
     * @param projectId The Synapse project ID
     * @returns Project information
     */
    const getProjectInfo = useCallback(
        async (projectId: string): Promise<SynapseProjectInfo | null> => {
            setLoading(true);
            setError(null);

            try {
                const endpoint = Endpoints.integrations.synapse.projectDetails(projectId);
                const response = await api.get<SynapseProjectResponse>(endpoint);

                if (response && response.entity) {
                    const entity = response.entity;
                    setLoading(false);
                    return {
                        id: entity.id,
                        name: entity.name,
                        description: entity.description,
                        createdOn: entity.createdOn,
                        modifiedOn: entity.modifiedOn,
                        createdBy: response.entityCreatedBy?.userName,
                    };
                }

                setLoading(false);
                return null;
            } catch (error) {
                logger.error('Error fetching Synapse project info:', error);
                setError('Failed to fetch project information');
                setLoading(false);
                return null;
            }
        },
        [api],
    );

    /**
     * Get the status of a specific workflow
     *
     * @param experimentId The experiment ID
     * @param workflowId The workflow ID (session ID)
     * @returns Workflow status
     */
    const getWorkflowStatus = useCallback(
        async (experimentId: string, workflowId: string): Promise<WorkflowStatus> => {
            setLoading(true);
            setError(null);

            try {
                const endpoint = Endpoints.integrations.synapse.workflow(experimentId, workflowId);
                const response = await api.get<WorkflowStatus>(endpoint);
                setLoading(false);
                return response || { status: [], overall_status: 'unknown' };
            } catch (error) {
                logger.error('Error fetching Synapse import workflow status:', error);
                setError('Failed to fetch workflow status');
                setLoading(false);
                throw error;
            }
        },
        [api],
    );

    /**
     * Get all workflows for an experiment
     *
     * @param experimentId The experiment ID
     * @returns List of workflows
     */
    const getWorkflows = useCallback(
        async (experimentId: string): Promise<Workflow[]> => {
            setLoading(true);
            setError(null);

            try {
                const endpoint = Endpoints.integrations.synapse.workflows(experimentId);
                const response = await api.get<Workflow[]>(endpoint);
                setLoading(false);
                return response || [];
            } catch (error) {
                logger.error('Error fetching Synapse import workflows:', error);
                setError('Failed to fetch workflows');
                setLoading(false);
                throw error;
            }
        },
        [api],
    );

    /**
     * Get all files from all workflows for an experiment
     *
     * @param experimentId The experiment ID
     * @returns Map of workflow IDs to their files
     */
    const getAllWorkflowFiles = useCallback(
        async (experimentId: string): Promise<Map<string, WorkflowFile[]>> => {
            setLoading(true);
            setError(null);

            try {
                // Get all workflows
                const workflows = await getWorkflows(experimentId);
                const workflowFilesMap = new Map<string, WorkflowFile[]>();

                // Get status for each workflow
                await Promise.all(
                    workflows.map(async (workflow) => {
                        try {
                            const status = await getWorkflowStatus(experimentId, workflow.session_id);
                            workflowFilesMap.set(
                                workflow.session_id,
                                status.status.map((file) => ({
                                    file_name: file.file_name,
                                    status: file.status,
                                })),
                            );
                        } catch (error) {
                            logger.error(`Error fetching status for workflow ${workflow.session_id}:`, error);
                            workflowFilesMap.set(workflow.session_id, []);
                        }
                    }),
                );

                setLoading(false);
                return workflowFilesMap;
            } catch (error) {
                logger.error('Error fetching all workflow files:', error);
                setError('Failed to fetch workflow files');
                setLoading(false);
                return new Map();
            }
        },
        [getWorkflows, getWorkflowStatus],
    );

    /**
     * Import FASTQ files from Synapse into a Pluto experiment
     *
     * @param params Parameters for importing FASTQ files
     * @returns Response with import status
     */
    const importFastqFiles = useCallback(
        async (params: ImportSynapseFastqFilesParams): Promise<ImportSynapseFastqFilesResponse> => {
            const { experimentId, fileIds } = params;
            setLoading(true);
            setError(null);

            if (!experimentId) {
                setLoading(false);
                return {
                    success: false,
                    message: 'Experiment ID is required',
                };
            }

            if (!fileIds || fileIds.length === 0) {
                setLoading(false);
                return {
                    success: false,
                    message: 'No files selected for import',
                };
            }

            try {
                logger.info(`Streaming ${fileIds.length} Synapse FASTQ files to experiment ${experimentId}`);

                const endpoint = Endpoints.integrations.synapse.streamUpload();
                const payload = {
                    entity_ids: fileIds,
                    gcs_path: `temp/fastqs`,
                    experiment_uuid: experimentId,
                };

                try {
                    // Use the api.post method from useApi
                    const response = await api.post<{ session_id?: string }>(endpoint, payload);

                    logger.info('Stream upload initiated:', response);

                    // Extract the session ID from the response if available
                    const sessionId = response?.session_id;

                    setLoading(false);
                    return {
                        success: true,
                        message: `Successfully initiated streaming of ${fileIds.length} FASTQ files from Synapse`,
                        sessionId: sessionId,
                        // We no longer create fake imported files here since we'll be polling for status
                    };
                } catch (error) {
                    logger.error('Error from API:', error);
                    setError('API error during import');
                    setLoading(false);
                    throw error;
                }
            } catch (error) {
                logger.error('Error streaming Synapse FASTQ files:', error);
                setError('Failed to stream FASTQ files');
                setLoading(false);
                return {
                    success: false,
                    message: 'Failed to stream FASTQ files from Synapse',
                };
            }
        },
        [api],
    );

    return {
        loading,
        error,
        getProjectInfo,
        getWorkflows,
        getAllWorkflowFiles,
        getWorkflowStatus,
        importFastqFiles,
    };
};

export default useSynapseImport;
